
import React, { useState, useEffect, useCallback } from "react";
import { User } from "@/api/entities";
import { UploadFile } from "@/api/integrations"; // Import UploadFile
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, Save, Building, MapPin, Phone, Mail, Globe, Upload, X } from "lucide-react"; // Import Upload and X icons
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"; // Import Select components

export default function CompanySettings() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // New state for upload status
  const [companyData, setCompanyData] = useState({
    company_name: "",
    company_logo: "", // New state for company logo URL
    logo_position: "center", // New state for logo position
    logo_size: "contain", // New state for logo size
    company_address: "",
    company_address_line2: "", // Added: Address Line 2
    company_city: "",
    company_state: "",
    company_zip: "",
    company_country: "",
    company_phone: "",
    company_email: "",
    company_website: "",
    company_description: ""
  });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const userData = await User.me();
      setCurrentUser(userData);

      if (userData.role !== 'admin') {
        navigate(createPageUrl("Settings"));
        return;
      }

      // Load company data from user entity (you can store company settings in a custom entity if needed)
      setCompanyData({
        company_name: userData.company_name || "",
        company_logo: userData.company_logo || "", // Initialize company_logo
        logo_position: userData.logo_position || "center", // Initialize logo_position
        logo_size: userData.logo_size || "contain", // Initialize logo_size
        company_address: userData.company_address || "",
        company_address_line2: userData.company_address_line2 || "", // Added: Initialize Address Line 2
        company_city: userData.company_city || "",
        company_state: userData.company_state || "",
        company_zip: userData.company_zip || "",
        company_country: userData.company_country || "",
        company_phone: userData.company_phone || "",
        company_email: userData.company_email || "",
        company_website: userData.company_website || "",
        company_description: userData.company_description || ""
      });
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setIsLoading(false);
  }, [navigate]); // navigate is a dependency for useCallback

  useEffect(() => {
    loadData();
  }, [loadData]); // loadData is now stable due to useCallback

  // Handle logo file upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const { file_url } = await UploadFile({ file }); // Use UploadFile integration
      setCompanyData(prev => ({ ...prev, company_logo: file_url }));
    } catch (error) {
      console.error("Error uploading logo:", error);
      // Optionally show a toast notification for error
    }
    setIsUploading(false);
  };

  // Handle removing the logo
  const handleRemoveLogo = () => {
    setCompanyData(prev => ({ ...prev, company_logo: "" }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await User.updateMyUserData(companyData);
      await loadData(); // Reload data to ensure UI reflects saved state correctly
    } catch (error) {
      console.error("Error saving company settings:", error);
      // Optionally show a toast notification for error
    }
    setIsSaving(false);
  };

  const handleChange = (field, value) => {
    setCompanyData(prev => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="bg-slate-50 p-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/4"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(createPageUrl("Settings"))}
          className="mb-6 text-slate-600 flex items-center gap-2 hover:scale-105 transition-transform duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Settings
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Company Settings</h1>
          <p className="text-slate-600">Configure your company information and branding</p>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Logo Settings Card */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Upload className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">Company Logo</CardTitle>
                  <CardDescription>Upload and customize your company logo</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-4">
                <Label>Logo Preview</Label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 bg-slate-50">
                  {companyData.company_logo ? (
                    <div className="space-y-4">
                      <div
                        className="w-full h-32 bg-white rounded-lg border border-slate-200 flex items-center justify-center overflow-hidden"
                        style={{
                          // Dynamically set justify-content based on logo_position for horizontal alignment
                          justifyContent: companyData.logo_position === 'flex-start' ? 'flex-start' : companyData.logo_position === 'flex-end' ? 'flex-end' : 'center',
                          // vertical alignment is handled by alignItems center by default
                        }}
                      >
                        <img
                          src={companyData.company_logo}
                          alt="Company Logo"
                          className="max-w-full max-h-full" // Ensure image fits within container
                          style={{
                            objectFit: companyData.logo_size,
                            objectPosition: companyData.logo_position // This handles both horizontal and vertical if specified in CSS, but flex aligns parent
                          }}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleRemoveLogo}
                        className="w-full border-rose-300 text-rose-600 hover:bg-rose-50"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Remove Logo
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                      <p className="text-sm text-slate-600 mb-4">Upload your company logo</p>
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={isUploading}
                        className="hidden" // Hide default file input
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload"> {/* Custom button to trigger file input */}
                        <Button
                          type="button"
                          variant="outline"
                          disabled={isUploading}
                          className="cursor-pointer"
                          onClick={() => document.getElementById('logo-upload').click()}
                        >
                          {isUploading ? 'Uploading...' : 'Choose File'}
                        </Button>
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Logo customization options, shown only if a logo is present */}
              {companyData.company_logo && (
                <>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="logo_position">Logo Position</Label>
                      <Select
                        value={companyData.logo_position}
                        onValueChange={(value) => handleChange('logo_position', value)}
                      >
                        <SelectTrigger className="border-slate-300">
                          <SelectValue placeholder="Select position" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="flex-start">Left</SelectItem>
                          <SelectItem value="center">Center</SelectItem>
                          <SelectItem value="flex-end">Right</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="logo_size">Logo Size</Label>
                      <Select
                        value={companyData.logo_size}
                        onValueChange={(value) => handleChange('logo_size', value)}
                      >
                        <SelectTrigger className="border-slate-300">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="contain">Fit (Contain)</SelectItem>
                          <SelectItem value="cover">Fill (Cover)</SelectItem>
                          <SelectItem value="scale-down">Scale Down</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Upload New Logo</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={isUploading}
                      className="border-slate-300"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Basic Information */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Building className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">Basic Information</CardTitle>
                  <CardDescription>Your company's basic details</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  value={companyData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  placeholder="Immigration Law Firm"
                  className="border-slate-300"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_description">Company Description</Label>
                <Textarea
                  id="company_description"
                  value={companyData.company_description}
                  onChange={(e) => handleChange('company_description', e.target.value)}
                  placeholder="Brief description of your company..."
                  className="h-24 border-slate-300"
                />
              </div>
            </CardContent>
          </Card>

          {/* Address Information */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <MapPin className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">Address</CardTitle>
                  <CardDescription>Physical location details</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="space-y-2">
                <Label htmlFor="company_address">Street Address</Label>
                <Input
                  id="company_address"
                  value={companyData.company_address}
                  onChange={(e) => handleChange('company_address', e.target.value)}
                  placeholder="123 Main Street"
                  className="border-slate-300"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_address_line2">Address Line 2</Label>
                <Input
                  id="company_address_line2"
                  value={companyData.company_address_line2}
                  onChange={(e) => handleChange('company_address_line2', e.target.value)}
                  placeholder="Suite, Floor, Building, etc."
                  className="border-slate-300"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="company_city">City</Label>
                  <Input
                    id="company_city"
                    value={companyData.company_city}
                    onChange={(e) => handleChange('company_city', e.target.value)}
                    placeholder="New York"
                    className="border-slate-300"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_state">State/Province</Label>
                  <Input
                    id="company_state"
                    value={companyData.company_state}
                    onChange={(e) => handleChange('company_state', e.target.value)}
                    placeholder="NY"
                    className="border-slate-300"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_zip">ZIP/Postal Code</Label>
                  <Input
                    id="company_zip"
                    value={companyData.company_zip}
                    onChange={(e) => handleChange('company_zip', e.target.value)}
                    placeholder="10001"
                    className="border-slate-300"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_country">Country</Label>
                  <Input
                    id="company_country"
                    value={companyData.company_country}
                    onChange={(e) => handleChange('company_country', e.target.value)}
                    placeholder="United States"
                    className="border-slate-300"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Phone className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">Contact Information</CardTitle>
                  <CardDescription>How clients can reach you</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="company_phone">Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      id="company_phone"
                      value={companyData.company_phone}
                      onChange={(e) => handleChange('company_phone', e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      className="pl-10 border-slate-300"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company_email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      id="company_email"
                      type="email"
                      value={companyData.company_email}
                      onChange={(e) => handleChange('company_email', e.target.value)}
                      placeholder="contact@company.com"
                      className="pl-10 border-slate-300"
                    />
                  </div>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="company_website">Website</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      id="company_website"
                      value={companyData.company_website}
                      onChange={(e) => handleChange('company_website', e.target.value)}
                      placeholder="https://www.company.com"
                      className="pl-10 border-slate-300"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={loadData}
              disabled={isSaving || isUploading} // Disable cancel if uploading
              className="border-slate-300"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving || isUploading} // Disable save if saving or uploading
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
